import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

function formatPrice(price) {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
  }).format(price);
}

export default function CartDrawer() {
  const { items, drawerOpen, toggleDrawer, removeItem, updateQty, totalItems, totalPrice, hasCustomItems } = useCart();

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[80] transition-opacity duration-300 ${
          drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => toggleDrawer(false)}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-cream z-[90] shadow-2xl transition-transform duration-500 ease-in-out flex flex-col ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-wood/10">
          <div>
            <h2 className="font-serif text-2xl text-wood">Mi Selección</h2>
            <p className="font-sans text-xs text-taupe tracking-wider mt-1">
              {totalItems} {totalItems === 1 ? 'artículo' : 'artículos'}
            </p>
          </div>
          <button
            onClick={() => toggleDrawer(false)}
            className="w-10 h-10 flex items-center justify-center text-taupe hover:text-wood transition-colors"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <svg className="w-16 h-16 text-taupe/20 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                  d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
              <p className="font-serif text-xl text-wood mb-2">Tu selección está vacía</p>
              <p className="font-sans text-sm text-taupe mb-6">Agrega muebles para solicitar tu cotización</p>
              <button onClick={() => toggleDrawer(false)} className="btn-outline text-xs">
                Ver Colección
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 p-3 bg-cream-light rounded-sm border border-wood/5">
                  {/* Image */}
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-sm flex-shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-base text-wood leading-tight truncate">{item.name}</h4>
                    <p className="font-sans text-[10px] tracking-[0.15em] uppercase text-terracotta/70 mt-0.5">
                      {item.category}
                    </p>

                    {item.price ? (
                      <p className="font-sans text-sm font-semibold text-wood mt-1">
                        {formatPrice(item.price)}
                      </p>
                    ) : (
                      <p className="font-sans text-xs text-gold font-medium mt-1">Precio a cotizar</p>
                    )}

                    {/* Qty controls */}
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-wood/15 rounded-sm">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="w-7 h-7 flex items-center justify-center text-taupe hover:text-terracotta transition-colors text-sm"
                          aria-label="Reducir cantidad"
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-sans text-sm text-wood">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="w-7 h-7 flex items-center justify-center text-taupe hover:text-terracotta transition-colors text-sm"
                          aria-label="Aumentar cantidad"
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-taupe/50 hover:text-terracotta transition-colors ml-auto"
                        aria-label="Eliminar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-wood/10 px-6 py-5 bg-cream-light">
            {/* Subtotal */}
            <div className="space-y-2 mb-5">
              {totalPrice > 0 && (
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-taupe">Subtotal estimado</span>
                  <span className="text-wood font-semibold">{formatPrice(totalPrice)}</span>
                </div>
              )}
              {hasCustomItems && (
                <p className="font-sans text-xs text-gold">
                  * Algunos artículos requieren cotización personalizada
                </p>
              )}
              <p className="font-sans text-[10px] text-taupe/60 tracking-wider">
                Los precios son estimados. El precio final se confirma en la cotización.
              </p>
            </div>

            {/* Actions */}
            <Link
              to="/cotizacion"
              onClick={() => toggleDrawer(false)}
              className="btn-primary w-full text-center block mb-3"
            >
              Solicitar Cotización
            </Link>
            <button
              onClick={() => toggleDrawer(false)}
              className="btn-outline w-full text-center text-xs"
            >
              Seguir Explorando
            </button>
          </div>
        )}
      </div>
    </>
  );
}
